import { createApi } from '@reduxjs/toolkit/query/react';
import { config } from 'config';
import { PARTNER_ID, PRODUCT_CODE } from 'config/api.config';
import { DynamicCoverQuestion } from 'types/cover';
import {
  Industry,
  Occupation,
  OccupationCategory,
  OccupationsListItem,
  RefDataCover,
} from 'types/refData';

import { defaultHeadersCommercialInsurance } from './defaultHeaders';
import { createCustomBaseQuery } from './utils/customBaseQuery';

export interface GetIndustriesRequest {
  effectiveDate: string;
}

export interface GetIndustriesResponse {
  industries: Industry[];
  total: number;
}

export interface GetOccupationCategoriesRequest {
  industryCode: string;
  effectiveDate: string;
}

export interface GetOccupationCategoriesResponse {
  occupationCategories: OccupationCategory[];
  total: number;
}

export interface GetOccupationsRequest {
  industryCode: string;
  occupationCategory: string;
  effectiveDate: string;
}

export interface GetOccupationsResponse {
  occupations: Occupation[];
  total: number;
}

export interface GetOccupationsListRequest {
  effectiveDate: string;
  anzsics?: string[];
}

export interface GetOccupationsListResponse {
  occupations: OccupationsListItem[];
  total: number;
}

export interface GetDynamicCoverQuestionsRequest {
  anzsicCode: string;
  effectiveDate: string;
}

export interface GetDynamicCoverQuestionsResponse {
  total: number;
  covers: DynamicCoverQuestion[];
}

export interface GetCoversRequest {
  industryCode: string;
  effectiveDate: string;
}

export interface GetCoversResponse {
  total: number;
  covers: RefDataCover[];
}

// Use the reusable custom base query
const customBaseQuery = createCustomBaseQuery({
  baseUrl: `${config.msCommercialInsApi}/ref-data`,
  defaultHeaders: defaultHeadersCommercialInsurance,
  reducerPath: 'refDataApi',
});

/* Define a service using a base URL and expected endpoints */
export const refDataApi = createApi({
  reducerPath: 'refDataApi',
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    getIndustries: builder.query<GetIndustriesResponse, GetIndustriesRequest>({
      query: ({ effectiveDate }) =>
        `/industries?partnerId=${PARTNER_ID}&effectiveDate=${effectiveDate}`,
      keepUnusedDataFor: 3600 * 24 * 365,
    }),
    getOccupationCategories: builder.query<
      GetOccupationCategoriesResponse,
      GetOccupationCategoriesRequest
    >({
      query: ({ industryCode, effectiveDate }) =>
        `/industries/${industryCode}/occupation-categories?partnerId=${PARTNER_ID}&effectiveDate=${effectiveDate}`,
    }),
    getOccupations: builder.query<GetOccupationsResponse, GetOccupationsRequest>({
      query: ({ industryCode, occupationCategory, effectiveDate }) =>
        `/industries/${industryCode}/occupation-categories/${occupationCategory}/occupations?partnerId=${PARTNER_ID}&effectiveDate=${effectiveDate}`,
    }),
    getDyanmicCoverQuestions: builder.query<
      GetDynamicCoverQuestionsResponse,
      GetDynamicCoverQuestionsRequest
    >({
      query: ({ anzsicCode, effectiveDate }) =>
        `/cover-options?partnerId=${PARTNER_ID}&productCode=${PRODUCT_CODE}&anzsicCode=${anzsicCode}&effectiveDate=${effectiveDate}`,
    }),
    getOccupationsList: builder.query<GetOccupationsListResponse, GetOccupationsListRequest>({
      query: ({ effectiveDate, anzsics }) =>
        `/occupations?partnerId=${PARTNER_ID}&effectiveDate=${effectiveDate}${
          anzsics ? `&anzsics=${anzsics.join(',')}` : ''
        }`,
    }),
    getDynamicCovers: builder.query<GetCoversResponse, GetCoversRequest>({
      query: ({ industryCode, effectiveDate }) =>
        `/covers?partnerId=${PARTNER_ID}&productCode=${PRODUCT_CODE}&industryCode=${industryCode}&effectiveDate=${effectiveDate}`,
      keepUnusedDataFor: 3600, // 1 hour in seconds
    }),
  }),
});

export const {
  useGetIndustriesQuery,
  useLazyGetIndustriesQuery,
  useGetOccupationCategoriesQuery,
  useLazyGetOccupationCategoriesQuery,
  useGetOccupationsQuery,
  useLazyGetOccupationsQuery,
  useLazyGetDyanmicCoverQuestionsQuery,
  useLazyGetOccupationsListQuery,
  useGetOccupationsListQuery,
  useGetDynamicCoversQuery,
} = refDataApi;
